/**
 * <gldn-flora-stories> — center-mode scroll-snap carousel.
 *
 * - Arrow buttons scroll the track by one card width.
 * - Dots highlight the most-centered card (via IntersectionObserver
 *   with the track as root, threshold 0.7).
 * - Clicking a dot scrolls the corresponding card to center.
 *
 * Markup contract (see sections/gldn-flora-stories.liquid):
 *   <gldn-flora-stories>
 *     <div data-track>
 *       <article data-story data-story-index="0">...</article>
 *       ...
 *     </div>
 *     <button data-prev>‹</button>
 *     <button data-next>›</button>
 *     <div data-dots>
 *       <button data-dot data-dot-index="0">...</button>
 *       ...
 *     </div>
 *   </gldn-flora-stories>
 */
class GldnFloraStories extends HTMLElement {
  #track;
  #cards;
  #dots;
  #prev;
  #next;
  #observer;
  #activeIndex = 0;

  connectedCallback() {
    this.#track = this.querySelector('[data-track]');
    this.#cards = Array.from(this.querySelectorAll('[data-story]'));
    this.#dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.#prev = this.querySelector('[data-prev]');
    this.#next = this.querySelector('[data-next]');

    if (this.#cards.length < 2) return;

    this.#prev?.addEventListener('click', () => this.#go(this.#activeIndex - 1));
    this.#next?.addEventListener('click', () => this.#go(this.#activeIndex + 1));
    this.#dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const i = parseInt(dot.dataset.dotIndex, 10);
        this.#go(i);
      });
    });

    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(this.#onIntersect, {
        root: this.#track,
        threshold: [0.6, 0.8, 1],
      });
      this.#cards.forEach((card) => this.#observer.observe(card));
    }

    this.#track.addEventListener('scroll', this.#updateArrows, { passive: true });
    this.#updateArrows();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #go(targetIndex) {
    const count = this.#cards.length;
    const i = Math.max(0, Math.min(count - 1, targetIndex));
    const card = this.#cards[i];
    if (!card || !this.#track) return;
    // Center the target card in the track viewport
    const trackRect = this.#track.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const offset = card.offsetLeft - this.#track.offsetLeft - (trackRect.width - cardRect.width) / 2;
    this.#track.scrollTo({ left: offset, behavior: 'smooth' });
  }

  #onIntersect = (entries) => {
    // Pick the most-visible card and mark it active.
    let bestRatio = 0;
    let bestIndex = this.#activeIndex;
    entries.forEach((entry) => {
      if (entry.intersectionRatio > bestRatio) {
        bestRatio = entry.intersectionRatio;
        bestIndex = parseInt(entry.target.dataset.storyIndex, 10);
      }
    });
    if (bestRatio > 0.6 && bestIndex !== this.#activeIndex) {
      this.#setActive(bestIndex);
    }
  };

  #setActive(index) {
    this.#activeIndex = index;
    this.#dots.forEach((dot, i) => {
      const active = i === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  }

  #updateArrows = () => {
    if (!this.#track) return;
    const maxScroll = this.#track.scrollWidth - this.#track.clientWidth - 1;
    if (this.#prev) this.#prev.disabled = this.#track.scrollLeft <= 0;
    if (this.#next) this.#next.disabled = this.#track.scrollLeft >= maxScroll;
  };
}

if (!customElements.get('gldn-flora-stories')) {
  customElements.define('gldn-flora-stories', GldnFloraStories);
}
